// Shared CoPilot combo-pricing math (CR-20260619-001, Close-All increment).
//
// Extracted VERBATIM from HgCopilotSolutionCardComponent so the per-hedge Solution card AND the
// aggregate "Close All / Chosen" card compute the SAME ledger/order prices — single source of truth.
// The aggregate's header price is the SUM of each child's ledger price, so it MUST use the identical
// math (ES->SPX normFactor + portfolio defaultQty normalization + all-or-nothing blanking); a second
// copy of this arithmetic would be free to drift. Pure (no Angular, no quote-cache dependency): the
// caller injects the per-leg quote-mid + normFactor lookups, mirroring the card's getters.

import { Leg } from './copilot-domain';
import { findHCF } from '../../utils';

// The two pieces every CoPilot combo price is derived from: the signed net (sum of per-leg
// (sell:+ / buy:-) mid * |qty| * ES-normFactor) and the HCF of the leg quantities.
export interface ComboNet {
    net: number;
    hcf: number;
}

// Mirrors HgCopilotSolutionCardComponent.comboNet (pre-extraction). Returns undefined until ALL legs
// price (all-or-nothing, like the platform getGroupTotal). getQuoteMid(ticker) -> the option mid (or
// undefined while a quote is still streaming); getNormFactor(asset) -> per-leg ES->SPX multiplier
// (1 for SPX/base, 0.5 for ES futures).
export function computeComboNet(
    legs: Leg[] | null | undefined,
    getQuoteMid: (ticker: string) => number | undefined,
    getNormFactor: (asset: string) => number
): ComboNet | undefined {
    if (!legs || legs.length === 0) {
        return undefined;
    }
    const hcf = findHCF(legs.map(l => Math.abs(l.quantity)));
    let net = 0;
    for (const leg of legs) {
        const price = getQuoteMid(leg.ticker);
        if (price === undefined) {
            return undefined;   // all-or-nothing, like the platform getGroupTotal
        }
        const nf = getNormFactor(leg.asset || '');   // ES->SPX per leg (1 for SPX/base)
        const signed = leg.side === 'sell' ? 1 : -1;   // SELL credit (+), BUY debit (-)
        net += signed * price * Math.abs(leg.quantity) * nf;   // px * |qty| * normFactor (getGroupTotal)
    }
    return { net, hcf };
}

// Order price = the net for the minimal (HCF-reduced) combo, WITHOUT the per-portfolio default-quantity
// normalization (net / HCF). The value the Order Verification Dialog labels "Order Placement Price".
// Shown in the expanded Total row. undefined until ALL legs price.
export function orderPrice(c: ComboNet | undefined): number | undefined {
    return c === undefined ? undefined : c.net / c.hcf;
}

// Ledger price (credit > 0 / debit < 0) = the order price scaled to the portfolio default quantity:
// (net / HCF) * (HCF / defaultQty) == net / defaultQty (the HCF cancels). Skip the scale when defaultQty
// is missing/<=0 (ratio 1) — then ledger == order. Shown in the card header (top-right) and summed for
// the aggregate. undefined until ALL legs price.
export function ledgerPrice(c: ComboNet | undefined, defaultQty: number | undefined): number | undefined {
    if (c === undefined) {
        return undefined;
    }
    let net = c.net / c.hcf;   // order price: divide the TOTAL by HCF (getGroupTotal)
    if (typeof defaultQty === 'number' && !isNaN(defaultQty) && defaultQty > 0) {
        net = net * (c.hcf / defaultQty);   // scale to portfolio default qty (the ledger normalization)
    }
    return net;
}

// ---- THE RISK BAND, READ OFF THE TITLE (CR-20260824-003) -------------------------------------
//
// ⚠ THE WIRE CARRIES NO RISK FIELD, AND THIS IS NOT AN OVERSIGHT TO "FIX" HERE. Max risk was
// dropped from the Solution contract on 2026-06-19 (CoPilotSolutionDto has no risk member): it is
// a questionnaire INPUT, and the skill stopped reporting it as an attribute of an answer. The one
// place a package's own level survives is its TITLE, minted by PackageSolutionMapper.DescribeName
// as "[RISK MODE] - [Package Name]" — and only on a fanned-out "Max Risk: All" pass,
// because a trader who asked for one level is not retold what they chose (owner, 2026-08-19).
//
// So this parses the title, deliberately, rather than inventing a field the backend does not send.
// The consequence is stated where it bites: risk sorting is OFFERED ONLY when the result set
// actually holds more than one band, and is shown disabled otherwise (see riskBandsPresent).
//
// The alt engine's names keep the prefix ahead of its own marker — "Moderate - [alt] Package #4 -
// 12 legs" (SideBySideAdjustEngineRunner.RenameAltSolutions preserves it verbatim) — so anchoring
// at index 0 reads classic and alt packages alike.
export type RiskBand = 'Aggressive' | 'Moderate' | 'Low';

// HIGHEST RISK FIRST is rank 0, which makes "descending risk" a plain ascending rank compare and
// matches the owner's own reading order: "aggressive, moderate, low".
const RISK_BAND_RANK: { band: RiskBand; prefix: string }[] = [
    { band: 'Aggressive', prefix: 'aggressive - ' },
    { band: 'Moderate', prefix: 'moderate - ' },
    { band: 'Low', prefix: 'low - ' }
];

/// The band a package announces in its own title, or undefined when it announces none (every
/// single-level run, every locally-built Close, the LLM path). Case-insensitive, anchored at the
/// start — a "Low" appearing anywhere else in a title is part of the name, not a level.
export function riskBandOf(displayName: string | null | undefined): RiskBand | undefined {
    if (!displayName) {
        return undefined;
    }
    const name = displayName.toLowerCase();
    for (let i = 0; i < RISK_BAND_RANK.length; i++) {
        if (name.indexOf(RISK_BAND_RANK[i].prefix) === 0) {
            return RISK_BAND_RANK[i].band;
        }
    }
    return undefined;
}

/// Rank for ordering: Aggressive 0, Moderate 1, Low 2. undefined for an unbanded package — the
/// caller treats that the way it treats an unpriced one, LAST in either direction, because a
/// package that never named a level cannot be ranked against ones that did.
export function riskBandRank(band: RiskBand | undefined): number | undefined {
    for (let i = 0; i < RISK_BAND_RANK.length; i++) {
        if (RISK_BAND_RANK[i].band === band) {
            return i;
        }
    }
    return undefined;
}

/// The distinct bands present in a set of titles. Two or more = the Risk key can actually reorder
/// something, which is the ONLY condition under which the panel enables it.
export function riskBandsPresent(displayNames: (string | null | undefined)[]): RiskBand[] {
    const found: RiskBand[] = [];
    for (let i = 0; i < displayNames.length; i++) {
        const band = riskBandOf(displayNames[i]);
        if (band !== undefined && found.indexOf(band) === -1) {
            found.push(band);
        }
    }
    return found;
}

// ---- THE PACKAGE ORDERING -------------------------------------------------------------------
//
// One direction per key. 'none' takes the key out of the comparison entirely — it does NOT mean
// "ascending by default", and both keys at 'none' is the engine's own order, untouched.
export type SortDirection = 'desc' | 'asc' | 'none';

export interface SortKeys {
    risk: SortDirection;    // 'desc' = Aggressive -> Moderate -> Low (the default reading order)
    price: SortDirection;   // 'desc' = best -> worst, credits above debits
}

// The package ordering. The price hierarchy is the owner's (2026-08-11 evening, extending the
// 2026-08-06 price rule); RISK was added ahead of it as the outer key (CR-20260824-003, owner:
// risk first, then price):
//
//   0. RISK BAND, when the key is on — Aggressive, Moderate, Low (reversed on 'asc');
//   1. best PRICE first, credits above debits (reversed on 'asc');
//   2. fewer TRADED LEGS — the legs actually bought or sold (the package's traded order);
//   3. fewer CARDS — the trades placed (cards within the package);
//   4. the incoming order — the engine's own ranking — as the final, stable tie-break.
//
// Pure and exported so it can be tested without a component, and so the panel has exactly one
// definition of "better". Cost is SIGNED here — a credit is positive, a debit negative — so
// "best" is plain descending. Anything still unpriced sorts LAST: an unpriced package cannot be
// ranked against a priced one, and putting it first or interleaving it would be a guess. The same
// goes for a package carrying no risk band.
//
// ⚠ RULES 2 AND 3 ARE NOT REVERSED BY 'asc', and that is deliberate. They are PRICE tie-breakers —
// the tidiness rule that settles two packages costing the same. Flipping them on the ascending
// pass would answer "worst price, and make it the messiest one too", which no one asked for.
//
// Prices tie AT THE CENT: the card shows two decimals, and a sub-cent float remainder from the
// ledger arithmetic must not outrank the leg rule with a difference the trader cannot see.
export function sortedByKeys<T>(
    items: T[],
    keys: SortKeys,
    riskOf: (item: T) => RiskBand | undefined,
    priceOf: (item: T) => number | undefined,
    tradedLegsOf: (item: T) => number,
    cardsOf: (item: T) => number): T[] {
    return items
        .map((item, index) => ({
            item,
            index,
            risk: riskBandRank(riskOf(item)),
            price: priceOf(item),
            legs: tradedLegsOf(item),
            cards: cardsOf(item)
        }))
        .sort((a, b) => {
            if (keys.risk !== 'none' && !(a.risk === undefined && b.risk === undefined)) {
                if (a.risk === undefined) {
                    return 1;               // unbanded last, in EITHER direction
                }
                if (b.risk === undefined) {
                    return -1;
                }
                if (a.risk !== b.risk) {
                    // rank 0 IS the most aggressive, so 'desc' is the ascending rank compare
                    return keys.risk === 'desc' ? a.risk - b.risk : b.risk - a.risk;
                }
            }
            if (keys.price !== 'none') {
                if (a.price === undefined && b.price === undefined) {
                    return a.index - b.index;
                }
                if (a.price === undefined) {
                    return 1;
                }
                if (b.price === undefined) {
                    return -1;
                }
                const aCents = Math.round(a.price * 100);
                const bCents = Math.round(b.price * 100);
                if (aCents !== bCents) {
                    // higher (credit) first on 'desc'
                    return keys.price === 'desc' ? bCents - aCents : aCents - bCents;
                }
                if (a.legs !== b.legs) {
                    return a.legs - b.legs;     // fewer legs bought or sold
                }
                if (a.cards !== b.cards) {
                    return a.cards - b.cards;   // fewer trades placed
                }
            }
            return a.index - b.index;       // stability, explicitly - never left to the engine's sort
        })
        .map(x => x.item);
}

/// The pre-CR-20260824-003 ordering, unchanged in behaviour and kept as the name the price-sort
/// spec and any price-only caller already use: risk out, price descending.
export function sortedByBestPrice<T>(
    items: T[],
    priceOf: (item: T) => number | undefined,
    tradedLegsOf: (item: T) => number,
    cardsOf: (item: T) => number): T[] {
    return sortedByKeys(items, { risk: 'none', price: 'desc' },
        () => undefined, priceOf, tradedLegsOf, cardsOf);
}

// Dollar-formatted price: "$1.25" / "-$2.00"; blank ("—") when not yet priced. Mirrors the card.
export function formatMoney(value: number | undefined | null): string {
    if (value === undefined || value === null || isNaN(value)) {
        return '—';   // em dash
    }
    return (value < 0 ? '-$' : '$') + Math.abs(value).toFixed(2);
}

// ---- R9 (CR-20260805-001): hide answers that cost more than the minimum profit ----------------
//
// Owner: "If the price of the package is more expensive than the Minimum Profit, we pay for the
// package more than the Minimum Profit. Such things should be hidden from the screen and filtered
// out." Paying 16 to guarantee 15 is a losing trade whatever its geometry. Frontend-only,
// necessarily — the engine does not price (2026-07-28); this is the same seam as R6: the engine
// enumerates, the panel prices, and now also filters.
//
// UNITS, pinned by copilot-min-profit-filter.spec.ts: both figures are compared on the SAME
// normalized (per-default-quantity) basis, DIRECTLY. A normalized minimum profit of 15 compares
// against a package's ledger price of e.g. -1.09 as 1.09 vs 15 — never re-scale either side by
// defaultQty x 100, or the filter is wrong by a factor of 1,000 at lot 10. The ledger price
// (net / defaultQty — the card header figure) is the package price already measured per default
// quantity, which is exactly the basis a NORMALIZED minimum profit is stated in; for the ordinary
// package traded at the default lot it equals the Order Price the ruling was demonstrated on.

// The run's minimum profit as normalized points, whatever mode it was typed in. A RAW (plain
// dollar) minimum needs a defaultQty x 100 division to reach the ledger basis (the backend no
// longer performs any such division - since 2026-08-13 it divides by the multiplier alone); without a
// usable default quantity the two figures cannot be reconciled and the filter must FAIL OPEN
// (undefined => nothing hidden) rather than hide answers on a guessed divisor.
export function minProfitAsPoints(
    minProfit: number,
    minProfitNormalized: boolean,
    defaultQty: number | undefined
): number | undefined {
    if (typeof minProfit !== 'number' || isNaN(minProfit) || minProfit <= 0) {
        return undefined;
    }
    if (minProfitNormalized) {
        return minProfit;   // already the comparison basis — DIRECT, no re-scaling (the 1000x trap)
    }
    if (typeof defaultQty !== 'number' || isNaN(defaultQty) || defaultQty <= 0) {
        return undefined;
    }
    return minProfit / (defaultQty * 100);
}

// Whether ONE package is hidden by R9. ledger = the package's ledger price (credit > 0 / debit < 0),
// undefined while any leg is still unpriced.
//  - Unpriced never hides: the filter cannot judge what it cannot price, and "everything was too
//    expensive" must never be minted out of missing quotes.
//  - Credits never hide: a package that PAYS the trader can never cost more than the minimum.
//  - A debit EQUAL to the minimum hides too [spec 2.5, my-call]: it nets exactly zero.
export function isTooExpensive(ledger: number | undefined, minPoints: number | undefined): boolean {
    if (minPoints === undefined || ledger === undefined || isNaN(ledger)) {
        return false;
    }
    if (ledger >= 0) {
        return false;   // a credit, or free
    }
    return (-ledger) >= minPoints - 1e-9;
}
